import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query'
import { queryKeys } from '@/lib/queryKeys'
import {
  listCalendars,
  getCalendar,
  createCalendar,
  updateCalendar,
  deleteCalendar,
  copyCalendar,
} from '@/api/calendars'
import type { ListCalendarsParams } from '@/api/calendars'
import type {
  CalendarHeaderCreate,
  CalendarHeaderUpdate,
  PaginatedCalendarsResponse,
  CalendarDetailResponse,
} from '@/types'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'
import { useDebounce } from '@/hooks/useDebounce'

interface UseCalendarsListParams {
  search: string
  isActive?: boolean
  page: number
  pageSize: number
}

export function useCalendarsList({
  search,
  isActive,
  page,
  pageSize,
}: UseCalendarsListParams) {
  const debouncedSearch = useDebounce(search, 300)
  const skip = (page - 1) * pageSize

  const filters: ListCalendarsParams = {
    search: debouncedSearch || undefined,
    is_active: isActive,
    skip,
    limit: pageSize,
  }

  return useQuery<PaginatedCalendarsResponse>({
    queryKey: queryKeys.calendars.list(filters as Record<string, unknown>),
    queryFn: () => listCalendars(filters),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 30,
  })
}

export function useCalendarsInfinite(search: string) {
  const debouncedSearch = useDebounce(search, 300)

  return useInfiniteQuery<PaginatedCalendarsResponse>({
    queryKey: queryKeys.calendars.listInfinite(debouncedSearch),
    queryFn: ({ pageParam = 0 }) =>
      listCalendars({
        search: debouncedSearch || undefined,
        is_active: true,
        skip: pageParam as number,
        limit: 20,
      }),
    getNextPageParam: lastPage =>
      lastPage.pagination.has_more
        ? lastPage.pagination.skip + lastPage.pagination.limit
        : undefined,
    initialPageParam: 0,
    staleTime: 1000 * 30,
  })
}

export function useCalendarDetail(calendarId: string | undefined) {
  return useQuery<CalendarDetailResponse>({
    queryKey: queryKeys.calendars.detail(calendarId ?? ''),
    queryFn: () => getCalendar(calendarId!),
    enabled: !!calendarId,
    staleTime: 1000 * 30,
  })
}

export function useCreateCalendar() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CalendarHeaderCreate) => createCalendar(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendars.all })
      queryClient.refetchQueries({ queryKey: queryKeys.calendars.all })
      toast.success('Calendar created successfully')
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to create calendar'))
    },
  })
}

export function useUpdateCalendar(calendarId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CalendarHeaderUpdate) =>
      updateCalendar(calendarId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendars.all })
      queryClient.invalidateQueries({
        queryKey: queryKeys.calendars.detail(calendarId),
      })
      toast.success('Calendar updated successfully')
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update calendar'))
    },
  })
}

export function useDeleteCalendar() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteCalendar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendars.all })
      toast.success('Calendar deleted successfully')
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to delete calendar'))
    },
  })
}

export function useCopyCalendar() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      copyCalendar(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendars.all })
      toast.success('Calendar copied successfully')
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to copy calendar'))
    },
  })
}
